import React, { useEffect, useState } from "react";

import { yupResolver } from "@hookform/resolvers/yup";
import {
  Button,
  CheckForm,
  Divider,
  Flex,
  Form,
  Grid,
  Group,
  Page,
  Section,
  TextArea,
} from "@samskip/frontend-components";
import { isBefore } from "date-fns";
import { PageProps, navigate } from "gatsby";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { nameof } from "ts-simple-nameof";

import { DomesticConstants, NavigationConstants } from "@assets/constants";
import { DomesticFormTypes } from "@assets/formTypes";
import { useAppDispatch, useAppSelector } from "@assets/hooks";
import { DomesticMappings } from "@assets/mappings";
import { DomesticTypes } from "@assets/types";
import { getMinDeliveryDate } from "@assets/utilities/dateUtilities";
import { swapFieldValues } from "@assets/utilities/formUtilities";
import { getErrorMessage } from "@assets/utilities/translateUtilities";
import { DomesticYup } from "@assets/yupObjects";
import { DomesticSSNValidationChecker } from "@assets/yupObjects/domestic";
import {
  DomesticBookingPartySection,
  DomesticCalendarSection,
  DomesticCargoSection,
  DomesticPaymentSection,
  DomesticReceiptDeliveryPlaceSection,
  DomesticRemoveCargoModal,
  LoadingModal,
  SpinnerPage,
} from "@components";
import DomesticService from "@services/DomesticService";
import UserService from "@services/UserService";
import {
  domesticPostCodesState,
  getDomesticItemConditions,
  getDomesticPostCodes,
} from "@state/slices/domesticSlice";
import { userProfileState } from "@state/slices/userProfileSlice";

const personSSNValidationChecker = new DomesticSSNValidationChecker();

interface DomesticBookingCreateProps extends PageProps {
  isKioskMode: boolean;
}

const DomesticBookingCreate = ({
  location,
  isKioskMode,
}: DomesticBookingCreateProps) => {
  const [isPageLoading, setIsPageLoading] = useState(true);

  const [senderDepartments, setSenderDepartments] = useState<
    DomesticTypes.DomesticDepartment[]
  >([]);
  const [receiverDepartments, setReceiverDepartments] = useState<
    DomesticTypes.DomesticDepartment[]
  >([]);

  const [minDeliveryDate, setMinDeliveryDate] = useState<Date>(
    getMinDeliveryDate()
  );
  const userProfile = useAppSelector(userProfileState).userProfile;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isChristmasPackageAvailable, setIsChristmasPackageAvailable] =
    useState(false);
  const [isCargoRemoveConfirmationOpen, setIsCargoRemoveConfirmationOpen] =
    useState(false);

  const { t } = useTranslation();
  const postCodes: DomesticTypes.DomesticPostCode[] = useAppSelector(
    domesticPostCodesState
  );
  const dispatch = useAppDispatch();

  const {
    clearErrors,
    control,
    formState: { errors },
    getValues,
    handleSubmit,
    register,
    setValue,
    trigger,
    watch,
  } = useForm({
    defaultValues: DomesticFormTypes.defaultFormValues,
    resolver: yupResolver(
      DomesticYup.domesticForm(
        personSSNValidationChecker,
        userProfile,
        senderDepartments,
        receiverDepartments
      )
    ),
    mode: "onTouched",
  });

  const isChristmasPackageChecked = watch("IsChristmasPackage");

  useEffect(() => {
    Promise.all([
      dispatch(getDomesticItemConditions()).unwrap(),
      dispatch(getDomesticPostCodes()).unwrap(),
      DomesticService.areChristmasPackagesAvailable(),
    ]).then(([, , isChristmasTime]) => {
      initWithSelectedCustomer().finally(() => {
        setIsPageLoading(false);
      });
      setIsChristmasPackageAvailable(isChristmasTime);
    });
  }, []);

  useEffect(() => {
    initWithSelectedCustomer();
  }, [userProfile?.SelectedCompany.PartnerCode]);

  const resetPayer = () => {
    setValue(
      nameof<DomesticFormTypes.CreateForm>((i) => i.Payer),
      DomesticFormTypes.defaultFormValues.Payer
    );
  };

  const resetIsPayerLegs = () => {
    setValue(
      nameof<DomesticFormTypes.CreateForm>((i) => i.IsSeparatePayerLegs),
      DomesticFormTypes.defaultFormValues.IsSeparatePayerLegs
    );
  };

  const resetPayerSectionFields = () => {
    resetIsPayerLegs();
    resetPayer();
  };

  // ==== BOOKING PARTY
  const populateAddressFields = async (
    bookingParty: DomesticConstants.BOOKING_PARTY,
    address: DomesticTypes.DomesticAddress | null
  ) => {
    const bookingPartyFieldNames =
      DomesticFormTypes.bookingPartyFieldNames[bookingParty];

    const addressFieldsToPopulate = {
      Address:
        DomesticFormTypes.defaultFormValues[bookingPartyFieldNames.Address],
      Email: DomesticFormTypes.defaultFormValues[bookingPartyFieldNames.Email],
      PostalCode:
        DomesticFormTypes.defaultFormValues[bookingPartyFieldNames.PostalCode],
      Phone: DomesticFormTypes.defaultFormValues[bookingPartyFieldNames.Phone],
    };

    if (address) {
      addressFieldsToPopulate.Address = address.Address;
      addressFieldsToPopulate.Email = address.Email;

      let postCode;
      try {
        postCode =
          postCodes.length > 0
            ? postCodes.find(
                (postCode) => postCode.PostCode === address.PostCode
              )
            : await DomesticService.getPostCode(address.PostCode);
      } catch {
        // Ignore error
      } finally {
        addressFieldsToPopulate.PostalCode = postCode;
      }
      addressFieldsToPopulate.Phone = address.PhoneNo;
    }

    if (!addressFieldsToPopulate.PostalCode && !isChristmasPackageChecked) {
      resetPayerSectionFields();
    }

    Object.keys(addressFieldsToPopulate).forEach((fieldName) => {
      setValue(
        bookingPartyFieldNames[fieldName],
        addressFieldsToPopulate[fieldName] ??
          DomesticFormTypes.defaultFormValues[bookingPartyFieldNames[fieldName]]
      );
      clearErrors(bookingPartyFieldNames[fieldName]);
    });
  };

  const resetDepartmentOptions = (
    bookingParty: DomesticConstants.BOOKING_PARTY
  ) => {
    if (bookingParty === DomesticConstants.BOOKING_PARTY.SENDER) {
      setSenderDepartments([]);
    } else {
      setReceiverDepartments([]);
    }
    setValue(
      DomesticFormTypes.bookingPartyFieldNames[bookingParty].Department,
      DomesticFormTypes.defaultFormValues[
        DomesticFormTypes.bookingPartyFieldNames[bookingParty].Department
      ]
    );
  };

  const resetBookingParty = (bookingParty: DomesticConstants.BOOKING_PARTY) => {
    const bookingPartyFieldNames =
      DomesticFormTypes.bookingPartyFieldNames[bookingParty];
    resetDepartmentOptions(bookingParty);
    const fieldsToReset = [
      nameof<DomesticFormTypes.BookingPartySectionFields>(
        (item) => item.Customer
      ),
      nameof<DomesticFormTypes.BookingPartySectionFields>((item) => item.SSN),
      nameof<DomesticFormTypes.BookingPartySectionFields>((item) => item.Name),
      nameof<DomesticFormTypes.BookingPartySectionFields>(
        (item) => item.IsNewAddress
      ),
      nameof<DomesticFormTypes.BookingPartySectionFields>(
        (item) => item.Address
      ),
      nameof<DomesticFormTypes.BookingPartySectionFields>(
        (item) => item.PostalCode
      ),
      nameof<DomesticFormTypes.BookingPartySectionFields>((item) => item.Phone),
      nameof<DomesticFormTypes.BookingPartySectionFields>((item) => item.Email),
    ] as unknown as (keyof DomesticFormTypes.BookingPartySectionFields)[];
    fieldsToReset.forEach((fieldName) => {
      setValue(
        bookingPartyFieldNames[fieldName],
        DomesticFormTypes.defaultFormValues[bookingPartyFieldNames[fieldName]]
      );
      clearErrors(bookingPartyFieldNames[fieldName]);
    });
    if (!isChristmasPackageChecked) {
      resetPayerSectionFields();
    }
  };

  const generateDepartments = (
    bookingParty: DomesticConstants.BOOKING_PARTY,
    party: DomesticTypes.DomesticCustomer | null
  ) => {
    const bookingPartyFieldNames =
      DomesticFormTypes.bookingPartyFieldNames[bookingParty];
    if (party?.No) {
      DomesticService.getDepartments(party?.No).then((departments) => {
        // Set the department options
        if (bookingParty === DomesticConstants.BOOKING_PARTY.SENDER) {
          setSenderDepartments(departments);
        } else {
          setReceiverDepartments(departments);
        }
        // If there are departments, automatically select the first one
        if (departments.length > 0) {
          populateAddressFields(bookingParty, departments[0]);
          setValue(bookingPartyFieldNames.Department, departments[0], {
            shouldValidate: true,
            shouldDirty: true,
          });
        } else {
          setValue(
            bookingPartyFieldNames.Department,
            DomesticFormTypes.defaultFormValues[
              bookingPartyFieldNames.Department
            ]
          );
          populateAddressFields(bookingParty, party);
        }
      });
    }
  };

  const handleCustomerChange = (
    bookingParty: DomesticConstants.BOOKING_PARTY,
    newCustomer: DomesticTypes.DomesticCustomer | null
  ) => {
    if (!newCustomer) {
      resetBookingParty(bookingParty);
    } else {
      const isCompany =
        watch(
          DomesticFormTypes.bookingPartyFieldNames[bookingParty]
            .BookingPartyType
        ) === DomesticConstants.BOOKING_PARTY_TYPE.COMPANY;

      setValue(
        DomesticFormTypes.bookingPartyFieldNames[bookingParty].SSN,
        newCustomer?.No ??
          DomesticFormTypes.defaultFormValues[
            DomesticFormTypes.bookingPartyFieldNames[bookingParty].SSN
          ]
      );
      setValue(
        DomesticFormTypes.bookingPartyFieldNames[bookingParty].Name,
        newCustomer?.Name ??
          DomesticFormTypes.defaultFormValues[
            DomesticFormTypes.bookingPartyFieldNames[bookingParty].Name
          ]
      );
      setValue(
        DomesticFormTypes.bookingPartyFieldNames[bookingParty].IsNewAddress,
        DomesticFormTypes.defaultFormValues[
          DomesticFormTypes.bookingPartyFieldNames[bookingParty].IsNewAddress
        ]
      );

      if (isCompany) {
        generateDepartments(bookingParty, newCustomer);
      } else {
        populateAddressFields(bookingParty, newCustomer);
      }
    }
  };

  // initialize the Sender DomesticCustomer based on user's selected company
  const initWithSelectedCustomer = async () => {
    if (userProfile) {
      const selectedBookingParty =
        getValues("BookingParty") ?? DomesticConstants.BOOKING_PARTY.SENDER;
      try {
        const selectedCustomer = await DomesticService.getSelectedCustomer();
        setValue(
          DomesticFormTypes.bookingPartyFieldNames[selectedBookingParty]
            .Customer,
          selectedCustomer
        );
        setValue(
          DomesticFormTypes.bookingPartyFieldNames[selectedBookingParty]
            .BookingPartyType,
          DomesticFormTypes.defaultFormValues[
            DomesticFormTypes.bookingPartyFieldNames[selectedBookingParty]
              .BookingPartyType
          ]
        );
        handleCustomerChange(selectedBookingParty, selectedCustomer);
      } catch {
        toast.error(t("ERROR_SELECTED_COMPANY_AUTOFILL"));
        setValue(
          DomesticFormTypes.bookingPartyFieldNames[selectedBookingParty]
            .BookingPartyType,
          DomesticConstants.BOOKING_PARTY_TYPE.PERSON
        );
        setValue(
          DomesticFormTypes.bookingPartyFieldNames[selectedBookingParty]
            .Customer,
          DomesticFormTypes.defaultFormValues[
            DomesticFormTypes.bookingPartyFieldNames[selectedBookingParty]
              .Customer
          ]
        );

        handleCustomerChange(
          selectedBookingParty,
          DomesticFormTypes.defaultFormValues[
            DomesticFormTypes.bookingPartyFieldNames[selectedBookingParty]
              .Customer
          ]
        );
      }
    }
  };

  const swapParties = () => {
    const bookingPartyFieldsToSwap = [
      nameof<DomesticFormTypes.BookingPartySectionFields>(
        (item) => item.BookingPartyType
      ),
      nameof<DomesticFormTypes.BookingPartySectionFields>(
        (item) => item.Customer
      ),
      nameof<DomesticFormTypes.BookingPartySectionFields>((item) => item.SSN),
      nameof<DomesticFormTypes.BookingPartySectionFields>(
        (item) => item.IsNewAddress
      ),
      nameof<DomesticFormTypes.BookingPartySectionFields>(
        (item) => item.Department
      ),
      nameof<DomesticFormTypes.BookingPartySectionFields>((item) => item.Name),
      nameof<DomesticFormTypes.BookingPartySectionFields>(
        (item) => item.Address
      ),
      nameof<DomesticFormTypes.BookingPartySectionFields>(
        (item) => item.PostalCode
      ),
      nameof<DomesticFormTypes.BookingPartySectionFields>((item) => item.Phone),
      nameof<DomesticFormTypes.BookingPartySectionFields>((item) => item.Email),
    ] as unknown as (keyof DomesticFormTypes.BookingPartySectionFields)[];

    bookingPartyFieldsToSwap.forEach((fieldName) => {
      swapFieldValues(
        DomesticFormTypes.bookingPartyFieldNames.SENDER[fieldName],
        DomesticFormTypes.bookingPartyFieldNames.RECEIVER[fieldName],
        getValues,
        setValue
      );
      clearErrors(DomesticFormTypes.bookingPartyFieldNames.SENDER[fieldName]);
      clearErrors(DomesticFormTypes.bookingPartyFieldNames.RECEIVER[fieldName]);
    });

    const tmp: DomesticTypes.DomesticDepartment[] = senderDepartments;
    setReceiverDepartments(tmp);
    setSenderDepartments(receiverDepartments);
  };

  const searchCustomers = (
    bookingParty: DomesticConstants.BOOKING_PARTY,
    searchString: string
  ) =>
    watch(
      DomesticFormTypes.bookingPartyFieldNames[bookingParty].BookingPartyType
    ) === DomesticConstants.BOOKING_PARTY_TYPE.COMPANY
      ? DomesticService.searchCompanyCustomers(searchString)
      : DomesticService.searchPersonCustomers(searchString);

  const populateAddressesBasedOnIsNewAddress = (
    bookingParty: DomesticConstants.BOOKING_PARTY,
    isNewAddress: boolean
  ) => {
    const departmentFieldName =
      DomesticFormTypes.bookingPartyFieldNames[bookingParty].Department;

    // Return the address of the department or of the customer if there is no department
    const getAddress = (addresses: DomesticTypes.DomesticAddress[]) => {
      switch (addresses.length) {
        case 0:
          return getValues(
            DomesticFormTypes.bookingPartyFieldNames[bookingParty].Customer
          );
        case 1:
          // Auto-select the only department
          setValue(departmentFieldName, addresses[0]);
          return addresses[0];
        default:
          // The department will need to be selected manually
          return null;
      }
    };

    let address = null;
    if (isNewAddress) {
      setValue(
        departmentFieldName,
        DomesticFormTypes.defaultFormValues[departmentFieldName]
      );
    } else {
      // After unchecking the address inputs need to be repopulated
      if (bookingParty === DomesticConstants.BOOKING_PARTY.SENDER) {
        address = getAddress(senderDepartments);
      } else {
        address = getAddress(receiverDepartments);
      }
    }
    populateAddressFields(bookingParty, address);
  };

  // ==== RECEIPT/DELIVERY
  const resetPlaceFields = (
    placesFieldNames: DomesticFormTypes.PlacesSectionFields
  ) => {
    const fieldsToReset = [
      nameof<DomesticFormTypes.PlacesSectionFields>((item) => item.ContactName),
      nameof<DomesticFormTypes.PlacesSectionFields>(
        (item) => item.ContactPhone
      ),
      nameof<DomesticFormTypes.PlacesSectionFields>((item) => item.Date),
    ] as unknown as (keyof DomesticFormTypes.PlacesSectionFields)[];

    fieldsToReset.forEach((fieldName) =>
      setValue(
        placesFieldNames[fieldName],
        DomesticFormTypes.defaultFormValues[placesFieldNames[fieldName]]
      )
    );
  };

  const copyPhone = (phoneField: string, newPhone: string) => {
    setValue(phoneField, newPhone);
    clearErrors(phoneField);
  };

  // ==== PAYMENT
  const getPayerLegAddresses = () => {
    const payerLegAddresses: DomesticTypes.DomesticPayerLegAddresses = {
      FromAddress: watch(
        nameof<DomesticFormTypes.CreateForm>((i) => i.SenderPostalCode)
      ),
      ToAddress: watch(
        nameof<DomesticFormTypes.CreateForm>((i) => i.ReceiverPostalCode)
      ),
    };
    return payerLegAddresses;
  };

  const resetEarlyDelivery = (newReceiptDate: Date | null) => {
    const newMinDelivery = getMinDeliveryDate(newReceiptDate ?? undefined);
    setMinDeliveryDate(newMinDelivery);
    // If the selected Date of delivery is earlier than the new minimum possible, then reset the delivery date to the new min
    const selectedDeliveryDate = getValues("DeliveryDate");
    if (
      selectedDeliveryDate &&
      isBefore(selectedDeliveryDate, newMinDelivery)
    ) {
      setValue(
        "DeliveryDate",
        DomesticFormTypes.defaultFormValues.DeliveryDate
      );
    }
  };

  const resetReceiptPlaceFields = () => {
    resetPlaceFields(DomesticFormTypes.placeFieldNames.PLR);
    resetIsPayerLegs();
    // Reset the delivery min date
    resetEarlyDelivery(null);
  };

  const handleDeliveryPlaceChange = () => {
    resetPlaceFields(DomesticFormTypes.placeFieldNames.PFD);
    resetIsPayerLegs();
  };

  // ==== CARGO
  const saveCargoDetail = (data: DomesticFormTypes.CargoDetailForm) => {
    const cargoDetails = getValues("CargoDetails");
    if (cargoDetails.some((cargo) => cargo.id === data.id)) {
      setValue(
        "CargoDetails",
        cargoDetails.map((cargo) => (cargo.id === data.id ? data : cargo)),
        {
          shouldValidate: true,
          shouldDirty: true,
        }
      );
    } else {
      setValue("CargoDetails", [...cargoDetails, data], {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  };

  const removeCargoDetail = (id: string) => {
    setValue(
      "CargoDetails",
      getValues("CargoDetails").filter((cargo) => cargo.id !== id),
      {
        shouldValidate: true,
        shouldDirty: true,
      }
    );
  };

  const handleChristmasPackageToggleIntent = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (getValues("CargoDetails").length > 0) {
      setIsCargoRemoveConfirmationOpen(true);
    } else {
      handleCheckChristmasPackageToggle(e.target.checked);
    }
  };

  const handleCheckChristmasPackageToggle = (isChristmasPackageOn: boolean) => {
    if (isChristmasPackageOn) {
      setValue("IsSeparatePayerLegs", false);
      setValue("Payer", DomesticConstants.BOOKING_PARTY.SENDER);
    } else {
      setValue("Payer", DomesticFormTypes.defaultFormValues.Payer);
    }
    setValue("CargoDetails", [], {
      shouldValidate: false,
      shouldDirty: false,
    });
    setValue(
      nameof<DomesticFormTypes.CreateForm>((i) => i.IsChristmasPackage),
      isChristmasPackageOn
    );
    setIsCargoRemoveConfirmationOpen(false);
  };

  const onSubmit = async (data: DomesticFormTypes.CreateForm) => {
    setIsSubmitting(true);
    try {
      const booking =
        DomesticMappings.domesticFormToCreateUpdateDomesticBooking(data);
      const bookingNumber = await DomesticService.createBooking(booking);
      // Not sure it's the best idea to pass in the search query, but at this moment I have no other idea how to pass in the noLayout for the kiosk...
      navigate(
        `${NavigationConstants.NAVIGATION_LINKS.domestic}/create/success${location.search}`,
        {
          state: { bookingNumber: bookingNumber, isKioskMode },
        }
      );
    } catch (err) {
      toast.error(
        t(
          err.response.data.ErrorItem.FriendlyMessage ??
            "ERROR_DOMESTIC_CREATE_BOOKING_FAILED"
        )
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isPageLoading) {
    return <SpinnerPage />;
  }

  return (
    <Page size="medium" title={t("MENU_CREATEBOOKING")}>
      <Form id="domestic_booking_form">
        {UserService.isUserCustomer(userProfile) && (
          <Group onChange={swapParties}>
            <CheckForm
              id="party_sender"
              label={t("LABEL_TOGGLE_SENDER")}
              type="radio"
              value={DomesticConstants.BOOKING_PARTY.SENDER}
              {...register("BookingParty")}
            />

            <CheckForm
              id="party_receiver"
              label={t("LABEL_TOGGLE_RECEIVER")}
              type="radio"
              value={DomesticConstants.BOOKING_PARTY.RECEIVER}
              {...register("BookingParty")}
            />
          </Group>
        )}
        <Grid mdCol={2}>
          <DomesticBookingPartySection
            control={control}
            departments={senderDepartments}
            errors={errors}
            isUserParty={
              getValues("BookingParty") ===
              DomesticConstants.BOOKING_PARTY.SENDER
            }
            onBookingPartyTypeChange={() =>
              resetBookingParty(DomesticConstants.BOOKING_PARTY.SENDER)
            }
            onCustomerChange={(newCustomer) =>
              handleCustomerChange(
                DomesticConstants.BOOKING_PARTY.SENDER,
                newCustomer
              )
            }
            onDepartmentChange={(newDepartment) =>
              populateAddressFields(
                DomesticConstants.BOOKING_PARTY.SENDER,
                newDepartment
              )
            }
            onNewAddressToggle={(isChecked) => {
              populateAddressesBasedOnIsNewAddress(
                DomesticConstants.BOOKING_PARTY.SENDER,
                isChecked
              );
            }}
            onPersonSSNChange={(personSSN: string) => {
              personSSNValidationChecker.validateSSN(personSSN, () =>
                trigger(
                  DomesticFormTypes.bookingPartyFieldNames[
                    DomesticConstants.BOOKING_PARTY.SENDER
                  ].SSN
                )
              );
            }}
            postalCodes={postCodes}
            register={register}
            registeredFields={
              DomesticFormTypes.bookingPartyFieldNames[
                DomesticConstants.BOOKING_PARTY.SENDER
              ]
            }
            searchCustomers={(searchString) =>
              searchCustomers(
                DomesticConstants.BOOKING_PARTY.SENDER,
                searchString
              )
            }
            showDepartments={senderDepartments.length > 1}
            title={t("LABEL_SENDER")}
          />

          <DomesticBookingPartySection
            control={control}
            departments={receiverDepartments}
            errors={errors}
            isUserParty={
              getValues("BookingParty") ===
              DomesticConstants.BOOKING_PARTY.RECEIVER
            }
            onBookingPartyTypeChange={() =>
              resetBookingParty(DomesticConstants.BOOKING_PARTY.RECEIVER)
            }
            onCustomerChange={(newCustomer) =>
              handleCustomerChange(
                DomesticConstants.BOOKING_PARTY.RECEIVER,
                newCustomer
              )
            }
            onDepartmentChange={(newDepartment) =>
              populateAddressFields(
                DomesticConstants.BOOKING_PARTY.RECEIVER,
                newDepartment
              )
            }
            onNewAddressToggle={(isChecked) => {
              populateAddressesBasedOnIsNewAddress(
                DomesticConstants.BOOKING_PARTY.RECEIVER,
                isChecked
              );
            }}
            onPersonSSNChange={(personSSN: string) => {
              personSSNValidationChecker.validateSSN(personSSN, () =>
                trigger(
                  DomesticFormTypes.bookingPartyFieldNames[
                    DomesticConstants.BOOKING_PARTY.RECEIVER
                  ].SSN
                )
              );
            }}
            postalCodes={postCodes}
            register={register}
            registeredFields={
              DomesticFormTypes.bookingPartyFieldNames[
                DomesticConstants.BOOKING_PARTY.RECEIVER
              ]
            }
            searchCustomers={(searchString) =>
              searchCustomers(
                DomesticConstants.BOOKING_PARTY.RECEIVER,
                searchString
              )
            }
            showDepartments={receiverDepartments.length > 1}
            title={t("LABEL_RECEIVER")}
          />
        </Grid>
        <Divider />
        <Grid mdCol={2}>
          <DomesticReceiptDeliveryPlaceSection
            control={control}
            errors={errors}
            onDeliveryModeChange={resetReceiptPlaceFields}
            onPhoneCopy={() =>
              copyPhone(
                DomesticFormTypes.placeFieldNames[
                  DomesticConstants.RECEIPT_DELIVERY_PLACES.PLR
                ].ContactPhone,
                getValues(
                  DomesticFormTypes.placeFieldNames[
                    DomesticConstants.RECEIPT_DELIVERY_PLACES.PLR
                  ].CustomerPhoneField
                )
              )
            }
            register={register}
            registeredFields={
              DomesticFormTypes.placeFieldNames[
                DomesticConstants.RECEIPT_DELIVERY_PLACES.PLR
              ]
            }
            title={t("LABEL_PLACE_OF_RECEIPT_DOMESTIC")}
            type={DomesticConstants.RECEIPT_DELIVERY_PLACES.PLR}
          />
          <DomesticReceiptDeliveryPlaceSection
            control={control}
            errors={errors}
            onDeliveryModeChange={handleDeliveryPlaceChange}
            onPhoneCopy={() =>
              copyPhone(
                DomesticFormTypes.placeFieldNames[
                  DomesticConstants.RECEIPT_DELIVERY_PLACES.PFD
                ].ContactPhone,
                getValues(
                  DomesticFormTypes.placeFieldNames[
                    DomesticConstants.RECEIPT_DELIVERY_PLACES.PFD
                  ].CustomerPhoneField
                )
              )
            }
            register={register}
            registeredFields={
              DomesticFormTypes.placeFieldNames[
                DomesticConstants.RECEIPT_DELIVERY_PLACES.PFD
              ]
            }
            title={t("LABEL_PLACE_OF_DELIVERY_DOMESTIC")}
            type={DomesticConstants.RECEIPT_DELIVERY_PLACES.PFD}
          />
        </Grid>
        <Divider />
        <DomesticCalendarSection
          control={control}
          errors={errors}
          minDeliveryDate={minDeliveryDate}
          onReceiptDateChange={resetEarlyDelivery}
        />
        <DomesticPaymentSection
          addresses={getPayerLegAddresses()}
          control={control}
          disabled={isChristmasPackageChecked}
          dropOffMethod={watch(
            nameof<DomesticFormTypes.CreateForm>((i) => i.DropOffMethod)
          )}
          errors={errors}
          onSeparatePayerToggle={resetPayer}
          pickupMethod={watch(
            nameof<DomesticFormTypes.CreateForm>((i) => i.PickupMethod)
          )}
          register={register}
        />
        <Divider />
        <DomesticCargoSection
          cargoDetails={getValues("CargoDetails")}
          errorMessage={getErrorMessage("CargoDetails", errors)}
          handleChristmasPackageToggle={handleChristmasPackageToggleIntent}
          isChristmasPackageAvailable={isChristmasPackageAvailable}
          isChristmasPackageChecked={isChristmasPackageChecked}
          onRemoveCargoDetail={removeCargoDetail}
          onSaveCargoDetail={saveCargoDetail}
        />
        <Divider />
        <Section id="notes" title={t("LABEL_COMMENTS")}>
          <Grid mdCol={3}>
            <TextArea
              className="col-span-2"
              formatMaxCharactersText={(count: number) =>
                t("TEXT_N_CHARACTERS_LEFT", { count: count })
              }
              fullWidth
              id="comment"
              maxLength={
                DomesticConstants.INPUT_CONSTRAINTS.MAX_COMMENTS_LENGTH
              }
              placeholder={t("PLACEHOLDER_OTHER_DETAILS")}
              rows={1}
              {...register("Comment")}
            />
          </Grid>
        </Section>
        <Divider />
        <div className="ml-auto">
          <Flex gap="small" justifyContent="between">
            {isKioskMode && (
              <Button
                onClick={() =>
                  navigate(
                    `${NavigationConstants.NAVIGATION_LINKS.domestic}/kiosk${location.search}`
                  )
                }
                variant="secondary"
              >
                {t("LABEL_BACK")}
              </Button>
            )}
            <Button
              form="domestic_booking_form"
              onClick={handleSubmit(onSubmit)}
            >
              {t("LABEL_SUBMIT")}
            </Button>
          </Flex>
        </div>
      </Form>
      <LoadingModal isOpen={isSubmitting}>
        {t("TEXT_LOADING_BOOKING")}
      </LoadingModal>
      <DomesticRemoveCargoModal
        isOpen={isCargoRemoveConfirmationOpen}
        onCancel={() => setIsCargoRemoveConfirmationOpen(false)}
        onSubmit={(willChristmasPackageBeChecked) =>
          handleCheckChristmasPackageToggle(willChristmasPackageBeChecked)
        }
        willChristmasPackageBeChecked={!isChristmasPackageChecked}
      />
    </Page>
  );
};

export default DomesticBookingCreate;
